import React, { FC, useState } from "react";
import { Container } from "../../../../components";
import { Icon, Heading, Button } from "@telia/teddy";
import cn from "classnames";
import _isEmpty from "lodash/isEmpty";
import { connect, useDispatch } from "react-redux";
import { isDeliveryNeeded } from "./utils";
import { DELIVERY_TYPE } from "../../../../../../constants";
import { DeliveryAddressSuggestion, Form, RootState } from "../../../../../../models";
import { DeliveryAddress } from "../../../../../../models/state";
import * as properNameCase from "../../../../../../shared/properNameCase";
import { trackFunnelEvent } from "../../../../google-analytics";
import { setDeliveryAddress } from "../../../state/actions/deliveryInfoActions";
import {
    isHwOnlyButRequireBankid,
    isHardwareOnly,
    isNewPhysicalSimRequired,
    isSimOnly,
} from "../../../state/selectors/combinedSelectors";
import { getDeliveryInfoAddress, getDeliveryInfoType } from "../../../state/selectors/deliveryInfoSelectors";
import TEXT from "../../../text-consts";
import "./ContactCard.less";
import EditDeliveryAddressForm from "../../organisms/EditDeliveryAddressForm/EditDeliveryAddressForm";

interface StateProps {
    deliveryAddress: Partial<DeliveryAddress>;
    isHwOnly: boolean;
    isHwOnlyBankid: boolean;
    isShippingSimRequired: boolean;
    isSimOnlyInCart: boolean;
}

interface ContactCardProps extends StateProps {
    address: string;
    city?: string;
    contactEmail?: string;
    contactName?: string;
    contactPhone?: string;
    deliveryAddressForm?: Form;
    headingText: string;
    isCheckout?: boolean;
    isSummary?: boolean;
    name: string;
    resetBankId?: () => void;
    showContactInfo: boolean;
    showLogoutBankId?: boolean;
}

const ContactCard: FC<ContactCardProps> = ({
    headingText,
    resetBankId,
    isSimOnlyInCart,
    name,
    address,
    city = "",
    contactName,
    isHwOnly,
    contactPhone,
    contactEmail,
    isShippingSimRequired,
    showContactInfo,
    showLogoutBankId,
    isCheckout = true,
    isSummary = false,
    deliveryAddress,
    deliveryAddressForm,
    isHwOnlyBankid = false,
}) => {
    const dispatch = useDispatch();
    const [displayEditDeliveryAddressForm, setDisplayEditDeliveryAddressForm] = useState(false);

    const orderNeedDeliveryAddressOption = isDeliveryNeeded({
        isSimOnly: isSimOnlyInCart,
        isShippingSimRequired,
        isHwOnly,
        isHwOnlyBankid,
    });

    const onSubmitDeliveryAddressForm = async (selectedAddress: DeliveryAddressSuggestion): Promise<void> => {
        if (selectedAddress) {
            await dispatch(setDeliveryAddress(selectedAddress));
            setDisplayEditDeliveryAddressForm(!displayEditDeliveryAddressForm);
        }
    };

    return (
        <Container
            className="contact-card-container__section"
            size="small"
            whiteBg
            noPaddingTop
            noMargin
            data-cy="contact-card"
        >
            {!_isEmpty(headingText) && (
                <Heading
                    mb="100"
                    as="h2"
                    variant="title-200"
                    className={cn({ "contact-card-container__heading": !isCheckout })}
                >
                    {headingText}
                </Heading>
            )}
            <div
                className={cn("contact-card-container__content", {
                    "contact-card-container__content-shadow": !isCheckout,
                })}
            >
                <div className="contact-card-container__wrapper fineprint" data-di-mask>
                    <div className="contact-card-container__address">
                        <div className="contact-card-container__heading-container">
                            <Heading as="h3" variant="title-100">
                                {properNameCase.convert(name)}
                            </Heading>
                            {isSummary && orderNeedDeliveryAddressOption && (
                                <Button
                                    variant="text"
                                    onClick={(): void => {
                                        setDisplayEditDeliveryAddressForm(!displayEditDeliveryAddressForm);
                                        trackFunnelEvent({
                                            name: "Mobil abo",
                                            isUIEvent: true,
                                            uiEventName: "change_address_start",
                                        });
                                    }}
                                >
                                    {!displayEditDeliveryAddressForm ? "Endre leveringsadresse" : "Avbryt"}
                                    <Icon name={!displayEditDeliveryAddressForm ? "edit" : "close"} />
                                </Button>
                            )}
                        </div>
                        {isSummary &&
                            displayEditDeliveryAddressForm &&
                            orderNeedDeliveryAddressOption &&
                            deliveryAddressForm && (
                                <EditDeliveryAddressForm
                                    deliveryAddressForm={deliveryAddressForm}
                                    onSubmitDeliveryAddressForm={onSubmitDeliveryAddressForm}
                                />
                            )}
                        {!displayEditDeliveryAddressForm && (
                            <>
                                {properNameCase.convert(address)}
                                <br />
                                {properNameCase.convert(city)}
                            </>
                        )}
                        {!_isEmpty(deliveryAddress) &&
                            ![deliveryAddress.street, deliveryAddress.postcode, deliveryAddress.city].every(
                                (item) => !item,
                            ) && (
                                <div className="contact-card-container__deliveryaddress-container">
                                    <Heading as="h3" variant="title-100">
                                        Leveringsadresse:
                                    </Heading>
                                    {properNameCase.convert(deliveryAddress.street || "")}
                                    <br />
                                    {properNameCase.convert(deliveryAddress.postcode || "") +
                                        " " +
                                        properNameCase.convert(deliveryAddress.city || "")}
                                </div>
                            )}
                    </div>
                    {showContactInfo && (
                        <div className="contact-card-container__contact">
                            <Heading as="h3" variant="title-100">
                                Kontaktinformasjon:
                            </Heading>
                            {!!contactName && (
                                <>
                                    {contactName}
                                    <br />
                                </>
                            )}
                            {contactPhone}
                            <br />
                            {contactEmail}
                        </div>
                    )}
                </div>
                {showLogoutBankId && (
                    <div className="contact-card-container__bankid-logout">
                        <Button
                            variant="text-underline"
                            type="button"
                            className="contact-card-container__bankid-btn"
                            onClick={resetBankId}
                        >
                            {TEXT.PERSONAL_DETAILS.VERIFICATION.RESET_BANKID}
                            <Icon className="contact-card-container__bankid-icon" name="logout" />
                        </Button>
                    </div>
                )}
            </div>
        </Container>
    );
};
const mapStateToProps = (state: RootState): StateProps => ({
    isHwOnlyBankid: isHwOnlyButRequireBankid(state),
    isHwOnly: isHardwareOnly(state),
    isSimOnlyInCart: isSimOnly(state),
    isShippingSimRequired: isNewPhysicalSimRequired(state),
    deliveryAddress:
        isSimOnly(state) && getDeliveryInfoType(state) === DELIVERY_TYPE.PICK_UP_IN_SHOP
            ? {}
            : getDeliveryInfoAddress(state),
});

export default connect(mapStateToProps)(ContactCard);
